import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  Pressable,
  SafeAreaView,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
  useWindowDimensions,
} from "react-native";
import { CheckIcon, XIcon } from "@phosphor-icons/react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import { AIRewriteActionButton, AIRewriteModal, useAIRewriteSuggestion } from "./AIRewriteControls";

interface FullContentEditorModalProps {
  currentContent: string;
  proposedContent: string;
  title: string;
  visible: boolean;
  isEditDisabled?: boolean;
  onClose: () => void;
  onSave?: (editedContent: string) => void;
}

function FullContentEditorModal({
  currentContent,
  proposedContent,
  title,
  visible,
  isEditDisabled,
  onClose,
  onSave,
}: FullContentEditorModalProps) {
  const { width } = useWindowDimensions();
  const showTwoColumns = width >= 900;
  const canEdit = Boolean(onSave) && !isEditDisabled;
  const [editingText, setEditingText] = useState(proposedContent);
  const getRewriteProposedText = useCallback(() => editingText, [editingText]);
  const {
    closeRewriteModal,
    isRewriteModalVisible,
    isRewriting,
    openRewriteModal,
    rewriteErrorMessage,
    submitRewrite,
  } = useAIRewriteSuggestion({
    currentText: currentContent,
    getProposedText: getRewriteProposedText,
    onApplyRewrite: setEditingText,
  });

  useEffect(() => {
    if (visible) {
      setEditingText(proposedContent);
    }
  }, [proposedContent, visible]);

  const handleSave = useCallback(() => {
    if (!canEdit) return;
    onSave?.(editingText);
    onClose();
  }, [canEdit, editingText, onClose, onSave]);

  return (
    <Modal visible={visible} animationType="fade" onRequestClose={onClose}>
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerTitleGroup}>
            <CustomText text={title} useTranslationText={false} size="s" weight="ultraBold" style={styles.title} />
            <CustomText text="Current vs proposed" useTranslationText={false} size="xs" style={styles.subtitle} />
          </View>
          <View style={styles.headerActions}>
            {canEdit && <AIRewriteActionButton disabled={isRewriting} onPress={openRewriteModal} />}
            {canEdit && (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel="Save full editor changes"
                onPress={handleSave}
                style={styles.saveButton}>
                <CheckIcon size={14} color={colors.white} weight="bold" />
                <CustomText
                  text="Save"
                  useTranslationText={false}
                  size="xs"
                  weight="medium"
                  style={styles.saveButtonText}
                />
              </Pressable>
            )}
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Close full editor"
              onPress={onClose}
              style={styles.closeButton}>
              <XIcon size={16} color={colors.grey[600]} weight="bold" />
            </Pressable>
          </View>
        </View>

        <View style={[styles.body, showTwoColumns ? styles.bodyDesktop : styles.bodyMobile]}>
          <View style={[styles.editorColumn, showTwoColumns && styles.editorColumnBorder]}>
            <View style={styles.columnHeader}>
              <CustomText
                text="Current"
                useTranslationText={false}
                size="s"
                weight="ultraBold"
                style={styles.currentLabel}
              />
            </View>
            <ScrollView style={styles.contentScroll} contentContainerStyle={styles.contentScrollInner}>
              <CustomText
                text={currentContent || "Not set"}
                useTranslationText={false}
                size="s"
                style={currentContent ? styles.bodyText : styles.emptyText}
              />
            </ScrollView>
          </View>

          <View style={styles.editorColumn}>
            <View style={[styles.columnHeader, styles.proposedHeader]}>
              <CustomText
                text="Proposed"
                useTranslationText={false}
                size="s"
                weight="ultraBold"
                style={styles.proposedLabel}
              />
            </View>
            {canEdit ? (
              <TextInput
                value={editingText}
                onChangeText={setEditingText}
                multiline
                style={styles.textarea}
                textAlignVertical="top"
                placeholder="Add proposed content"
                placeholderTextColor={colors.grey[500]}
              />
            ) : (
              <ScrollView style={styles.contentScroll} contentContainerStyle={styles.contentScrollInner}>
                <CustomText
                  text={proposedContent || "Not set"}
                  useTranslationText={false}
                  size="s"
                  style={proposedContent ? styles.bodyText : styles.emptyText}
                />
              </ScrollView>
            )}
          </View>
        </View>

        <AIRewriteModal
          errorMessage={rewriteErrorMessage}
          isRewriting={isRewriting}
          onCancel={closeRewriteModal}
          onRewrite={submitRewrite}
          visible={isRewriteModalVisible}
        />
      </SafeAreaView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  headerTitleGroup: {
    flex: 1,
  },
  title: {
    color: colors.grey[900],
  },
  subtitle: {
    color: colors.grey[500],
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  saveButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 7,
    borderRadius: 6,
    backgroundColor: colors.brand[600],
  },
  saveButtonText: {
    color: colors.white,
  },
  closeButton: {
    padding: 7,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.grey[200],
  },
  body: {
    flex: 1,
  },
  bodyDesktop: {
    flexDirection: "row",
  },
  bodyMobile: {
    flexDirection: "column",
  },
  editorColumn: {
    flex: 1,
    minHeight: 0,
  },
  editorColumnBorder: {
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
  },
  columnHeader: {
    height: 40,
    justifyContent: "center",
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  proposedHeader: {
    backgroundColor: "#F3FEE7",
  },
  currentLabel: {
    color: colors.grey[700],
  },
  proposedLabel: {
    color: colors.success[700],
  },
  contentScroll: {
    flex: 1,
  },
  contentScrollInner: {
    padding: 20,
  },
  bodyText: {
    color: colors.grey[800],
    fontSize: 14,
    lineHeight: 20,
  },
  emptyText: {
    color: colors.grey[400],
    fontSize: 14,
    lineHeight: 20,
    fontStyle: "italic",
  },
  textarea: {
    flex: 1,
    minHeight: 0,
    padding: 20,
    fontSize: 14,
    lineHeight: 20,
    color: colors.grey[900],
    backgroundColor: colors.white,
    borderWidth: 0,
    fontFamily: "Inter",
  },
});

export default FullContentEditorModal;
